import Vue from 'vue';
import types from '../mutation-types';
import authAPI from '../../api/auth';
import AccountAPI from '../../api/account';

import {
  setUser,
  clearCookiesOnLogout,
  clearLocalStorageOnLogout,
} from '../utils/api';
import { getLoginRedirectURL } from '../../helper/URLHelper';

const initialState = {
  currentUser: {
    id: null,
    account_id: null,
    accounts: [],
    email: null,
    name: null,
  },
  uiFlags: {
    isFetching: true,
    isUpdatingProfile: false,
  },
  embedding: false,
  currentAccount: null
};

const getTypeAccount = (account) => {
  if(!account) return null;
  if(account.parent_account){
    return 'subaccount';
  }else if(account.allow_subaccounts){
    return 'parent_account';
  }
  return 'account';

}

// getters
export const getters = {
  isLoggedIn($state) {
    return !!$state.currentUser.id;
  },

  getCurrentUserID($state) {
    return $state.currentUser.id;
  },

  getUISettings($state) {
    return $state.currentUser.ui_settings || {};
  },

  getAuthUIFlags($state) {
    return $state.uiFlags;
  },

  getCurrentUserAvailability($state, $getters) {    
    const currentAccount = $getters.getCurrentUserAccount;
    return currentAccount.availability;
  },

  getAutomaticReplyAvailabilities($state, $getters) {
    const currentAccount = $getters.getCurrentUserAccount;
    const replies = currentAccount.automatic_reply || [];
    return replies.flatMap(reply => reply.statuses || []);
  },

  getAutomaticReplies($state, $getters) {
    const currentAccount = $getters.getCurrentUserAccount;
    return currentAccount.automatic_reply || [];
  },

  getCurrentUserAutoOffline($state, $getters) {
    const currentAccount = $getters.getCurrentUserAccount;
    return currentAccount.auto_offline;
  },

  getCurrentAccountId(_, __, rootState) {
    if (rootState.route.params && rootState.route.params.accountId) {
      return Number(rootState.route.params.accountId);
    }else if(window.location.pathname.split('/')[3] != undefined && window.location.pathname.split('/')[2] == 'accounts' && /\d+?$/.test(window.location.pathname.split('/')[3])){
      return Number(window.location.pathname.split('/')[3]);
    }
    return null;
  },

  getCurrentRole($state, $getters) {
    const currentAccount = $getters.getCurrentUserAccount;
    return currentAccount.role;
  },

  getCurrentUser($state) {
    return $state.currentUser;
  },

  getMessageSignature($state) {
    const { message_signature: messageSignature } = $state.currentUser;

    return messageSignature || '';
  },

  getCurrentAccount($state) {
    return $state.currentAccount;    
  },

  getUserAccounts($state) {
    const { accounts = [] } = $state.currentUser;
    return accounts;
  },

  isEmbedding($state) {
    return $state.embedding;
  },

  getCurrentUserAccount($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    const [currentUserAccount = {}] = accounts.filter(
      account => account.id === $getters.getCurrentAccountId
    );
    return currentUserAccount || {};
  },

  getCurrentTypeAccount($state){
    return getTypeAccount($state.currentAccount);    
  },
  isAccountTypeParent($state) {
    return getTypeAccount($state.currentAccount) === 'parent_account';
  },
  allowCurrentAccount($state, $getters) {
    const { accounts = [] } = $state.currentUser;
    return accounts.some(
      account => account.id === $getters.getCurrentAccountId
    );    
  },
};

// actions
export const actions = {
  login(_, { ssoAccountId, ssoConversationId, embedding, ...credentials }) {
    return new Promise((resolve, reject) => {
      authAPI
        .login(credentials)
        .then(response => {
          clearLocalStorageOnLogout();
          window.location = getLoginRedirectURL({
            ssoAccountId,
            ssoConversationId,
            user: response.data,
            embedding: !!embedding
          });
          resolve();
        })
        .catch(error => {
          reject(error);
        });
    });
  },
  async validityCheck(context) {
    try {
      const response = await authAPI.validityCheck();
      const currentUser = response.data.payload.data;
      if(AccountAPI.accountIdFromRoute != "" || currentUser.account_id){
        try{
          let accountResponse = null;
          if(AccountAPI.accountIdFromRoute != ""){
            accountResponse = await AccountAPI.get();
          }else{
            accountResponse = await AccountAPI.show(currentUser.account_id);
          }          
          context.commit(types.SET_CURRENT_ACCOUNT, accountResponse.data);
        }catch(error){
          context.commit(types.SET_CURRENT_ACCOUNT, null);
        }
      }
      setUser(currentUser);
      context.commit(types.SET_CURRENT_USER, currentUser);
    } catch (error) {
      if (error?.response?.status === 401) {
        clearCookiesOnLogout();
      }
    }
  },
  async setUser({ commit, dispatch }) {
    if (authAPI.hasAuthCookie()) {
      await dispatch('validityCheck');
    } else {
      commit(types.CLEAR_USER);
    }
    commit(types.SET_CURRENT_USER_UI_FLAGS, { isFetching: false });
  },
  logout({ commit }) {
    commit(types.CLEAR_USER);
  },

  updateProfile: async ({ commit }, params) => {
    commit(types.SET_CURRENT_USER_UI_FLAGS, { isUpdatingProfile: true });
    try {
      const response = await authAPI.profileUpdate(params);
      commit(types.SET_CURRENT_USER, response.data);
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_CURRENT_USER_UI_FLAGS, { isUpdatingProfile: false });
    }
  },

  deleteAvatar: async () => {
    try {
      await authAPI.deleteAvatar();
    } catch (error) {
      // Ignore error
    }
  },

  updateUISettings: async ({ commit }, params) => {
    try {
      commit(types.SET_CURRENT_USER_UI_SETTINGS, params);
      const response = await authAPI.updateUISettings(params);
      commit(types.SET_CURRENT_USER, response.data);
    } catch (error) {
      // Ignore error
    }
  },

  updateAvailability: async ({ commit, dispatch }, params) => {
    try {
      const response = await authAPI.updateAvailability(params);
      const userData = response.data;
      const { id } = userData;
      commit(types.SET_CURRENT_USER, response.data);
      dispatch('agents/updateSingleAgentPresence', {
        id,
        availabilityStatus: params.availability,
      });
    } catch (error) {
      // Ignore error
    }
  },

  updateAutoOffline: async ({ commit }, { accountId, autoOffline }) => {
    try {
      const response = await authAPI.updateAutoOffline(accountId, autoOffline);
      commit(types.SET_CURRENT_USER, response.data);
    } catch (error) {
      // Ignore error
    }
  },

  setCurrentUserAvailability({ commit, state: $state }, data) {
    if (data[$state.currentUser.id]) {
      commit(types.SET_CURRENT_USER_AVAILABILITY, data[$state.currentUser.id]);
    }
  },

  setActiveAccount: async (_, { accountId }) => {
    try {
      await authAPI.setActiveAccount({ accountId });
    } catch (error) {
      // Ignore error
    }
  },

  setEmbedding({ commit }, enable) {
    commit(types.SET_EMBEDDING, enable);
  },

  verifyRequiredOtpCode: async () => {
    const response = await authAPI.verifyRequiredOtpCode();
    return response.data?.required || false;
  }
};

// mutations
export const mutations = {
  [types.SET_CURRENT_USER_AVAILABILITY](_state, availability) {
    const accounts = _state.currentUser.accounts.map(account => {
      if (account.id === _state.currentUser.account_id) {
        return { ...account, availability, availability_status: availability };
      }
      return account;
    });
    Vue.set(_state, 'currentUser', {
      ..._state.currentUser,
      accounts,
    });
  },
  [types.CLEAR_USER](_state) {
    _state.currentUser = initialState.currentUser;
  },
  [types.SET_CURRENT_USER](_state, currentUser) {
    Vue.set(_state, 'currentUser', currentUser);
  },
  [types.SET_CURRENT_USER_UI_SETTINGS](_state, { uiSettings }) {
    Vue.set(_state, 'currentUser', {
      ..._state.currentUser,
      ui_settings: {
        ..._state.currentUser.ui_settings,
        ...uiSettings,
      },
    });
  },

  [types.SET_CURRENT_USER_UI_FLAGS](_state, payload) {
    Vue.set(_state, 'uiFlags', {..._state.uiFlags, ...payload,});
  },

  [types.SET_EMBEDDING](_state, enable) {
    Vue.set(_state, 'embedding', enable);
  },
  [types.SET_CURRENT_ACCOUNT](_state, account) {
    Vue.set(_state, 'currentAccount', account);
  },
};

export default {
  state: initialState,
  getters,
  actions,
  mutations,
};
